import StudentGroupApi from "@apis/student.group.api";
import NotificationApi from "@apis/notification.api";
import NotificationEditContentSubpage from "../components/NotificationEditContentSubpage";
import NotificationViewContentSubpage from "@modules/notification/components/NotificationViewContentSubpage";
import SchoolNotificationEditContentSubpage from "../components/SchoolNotificationEditContentSubpage";
import i18n from "@plugins/i18n.ts";
import ContentSubpage from "@plugins/content-subpage";
import Toast from "@plugins/toast";
import Modal from "@plugins/modal";
import { NOTIFICATION_SCOPE } from "./notification.constant";
import SystemNotificationEditContentSubpage from "@modules/notification/components/SystemNotificationEditContentSubpage";

const onFail = () => {
  Toast.$toast.show("error", {
    kind: "error",
    title: i18n.t("api_fetch_generic_error_message"),
  });
};

const getViewApi = (announcementUuid, isManaged = false) => {
  return isManaged
    ? NotificationApi.fetchManageNotification(announcementUuid) //OK
    : NotificationApi.fetchViewerNotification(announcementUuid); //OK
};

const showContentSubpage = (mode, params, listeners, scope = "") => {
  let subpage = null;
  if (mode === "view") {
    subpage = NotificationViewContentSubpage;
  } else if (mode === "edit") {
    if (scope === NOTIFICATION_SCOPE.GROUP) {
      subpage = NotificationEditContentSubpage;
    } else if (scope === NOTIFICATION_SCOPE.SCHOOL) {
      subpage = SchoolNotificationEditContentSubpage;
    } else if (scope === NOTIFICATION_SCOPE.SYSTEM) {
      subpage = SystemNotificationEditContentSubpage;
    }
  }
  ContentSubpage.$contentSubpage.show(subpage, params, listeners);
};

const showNotificationView = (announcementUuid, subpageConfig) => {
  getViewApi(announcementUuid, subpageConfig.params.isManage).then(
    (response) => {
      if (response.code === "error") {
        handleError(
          response.errorMap[0],
          "view",
          announcementUuid,
          subpageConfig.listeners.postDelete,
        );
      }
      if (response.data) {
        let baseTitle = "";
        const scope = subpageConfig.params.scope;
        switch (scope) {
          case NOTIFICATION_SCOPE.GROUP:
            baseTitle = subpageConfig.params?.baseTitle ?? i18n.t("announcement_class_group_title");
            break;
          case NOTIFICATION_SCOPE.SCHOOL:
            baseTitle = i18n.t("announcement_school_title");
            break;
          case NOTIFICATION_SCOPE.SYSTEM:
            baseTitle = i18n.t("announcement_system_title");
            break;
        }
        showContentSubpage(
          "view",
          {
            baseTitle: [baseTitle],
            kind: subpageConfig.params.kind,
            data: {
              notification: response.data,
              canEdit: subpageConfig.params.canEdit,
              scope: scope,
              classGroup: subpageConfig.params?.classGroup ?? null,
            },
          },
          {
            showEdit: subpageConfig.listeners.showEdit,
            delete: subpageConfig.listeners.delete,
            routeToNotification: subpageConfig.listeners.routeToNotification,
          },
        );
      }
    },
    () => {
      onFail();
    },
  );
};

const showNotificationEdit = (announcementUuid, subpageConfig) => {
  getViewApi(announcementUuid, true).then(
    (response) => {
      if (response.code === "error") {
        handleError(
          response.errorMap[0],
          "edit",
          announcementUuid,
          subpageConfig.listeners.postDelete,
        );
        return;
      }

      if (response.data) {
        const scope = subpageConfig.params.scope;
        const classGroup = subpageConfig.params.classGroup;

        showContentSubpage(
          "edit",
          {
            baseTitle: [subpageConfig.params.baseTitle],
            kind: subpageConfig.params.kind,
            data: {
              notification: response.data,
              scope: scope,
              classGroup: classGroup,
            },
          },
          {
            update:
              typeof subpageConfig.listeners.update === "function"
                ? subpageConfig.listeners.update
                : () => {},
            delete: subpageConfig.listeners.delete,
            error: handleError,
          },
          scope,
        );
      }
    },
    () => {
      onFail();
    },
  );
};

const showNotificationCreate = (subpageConfig) => {
  StudentGroupApi.fetchMyActiveStudentGroups().then(
    //OK
    (response) => {
      if (response.code === "error") {
        onFail();
        return;
      }
      if (response.data) {
        showContentSubpage(
          "edit",
          {
            baseTitle: subpageConfig.params.baseTitle,
            kind: subpageConfig.params.kind,
            data: {
              canSubmit: subpageConfig.params.canSubmit,
              scope: subpageConfig.params.scope,
              studentGroupData: response.data,
            },
          },
          {
            create: subpageConfig.listeners.create,
          },
          NOTIFICATION_SCOPE.GROUP,
        );
      }
    },
    () => {
      onFail();
    },
  );
};

const showSchoolNotificationCreate = (subpageConfig) => {
  showContentSubpage(
    "edit",
    {
      baseTitle: subpageConfig.params.baseTitle,
      kind: subpageConfig.params.kind,
      data: {
        canSubmit: subpageConfig.params.canSubmit,
        scope: subpageConfig.params.scope,
        schoolData: subpageConfig.params.schoolData,
      },
    },
    {
      create: subpageConfig.listeners.create,
    },
    NOTIFICATION_SCOPE.SCHOOL,
  );
};

const handleError = (error, action = "", uuid = "", postDelete) => {
  if (error === "AnnouncementNotFound") {
    handleNotFound(action);
    typeof postDelete === "function" && postDelete(uuid);
  } else if (error === "AnnouncementAccessDenied") {
    handleAccessDenied();
  }
};

const handleNotFound = (action) => {
  const title = i18n.t("announcement_modal_title");
  const content = i18n.t("announcement_deleted_warning_content", { action });
  const props = {
    title: title,
    content: content,
    primaryBtnLabel: i18n.t("button_ok"),
    use_secondaryButton: false,
    actionPrimary: () => {},
    actionHidden: () => {},
  };
  Modal.$modal.showMessage("warning", props);
};

const handleAccessDenied = () => {
  const title = i18n.t("announcement_modal_title");
  const content = i18n.t("announcement_access_denied_message");
  const props = {
    title: title,
    content: content,
    primaryBtnLabel: i18n.t("button_ok"),
    use_secondaryButton: false,
    actionPrimary: () => {},
    actionHidden: () => {},
  };
  Modal.$modal.showMessage("warning", props);
};

export default {
  showNotificationView,
  showNotificationEdit,
  showNotificationCreate,
  showSchoolNotificationCreate,
};
